import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Save, AlertCircle } from 'lucide-react';
import { EmpresasAdminService } from '../../../services';
import type { CreateEmpresaRequest } from '../../../types';

const empresaSchema = z.object({
  nombre: z.string().min(2, 'Nombre debe tener al menos 2 caracteres').max(100, 'Nombre demasiado largo'),
  rut: z.string().min(8, 'RUT debe tener al menos 8 caracteres').max(12, 'RUT demasiado largo'),
  razon_social: z.string().min(2, 'Razón social debe tener al menos 2 caracteres').max(200),
  direccion: z.string().min(5, 'Dirección debe tener al menos 5 caracteres').max(200),
  telefono: z.string().optional().or(z.literal('')),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  giro: z.string().min(2, 'Giro debe tener al menos 2 caracteres').max(200),
  representante_legal: z.string().min(2, 'Representante legal debe tener al menos 2 caracteres').max(100),
  permite_multiple_faenas: z.boolean().default(true),
  requiere_aprobacion_reportes: z.boolean().default(false)
});

type EmpresaFormData = z.infer<typeof empresaSchema>;

interface EmpresaFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  empresaId?: number;
}

const EmpresaForm: React.FC<EmpresaFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  empresaId
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const isEditing = !!empresaId;

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<EmpresaFormData>({
    resolver: zodResolver(empresaSchema),
    defaultValues: { permite_multiple_faenas: true, requiere_aprobacion_reportes: false }
  });

  // Cargar datos de la empresa cuando se está editando
  useEffect(() => {
    const cargarDatosEmpresa = async () => {
      if (!isEditing || !empresaId || !isOpen) return;
      
      try {
        setLoadingData(true);
        setError(null);
        
        const empresa = await EmpresasAdminService.obtenerEmpresa(empresaId) as any;
        
        // Cargar datos en el formulario
        setValue('nombre', empresa.nombre);
        setValue('rut', empresa.rut);
        setValue('razon_social', empresa.razon_social);
        setValue('direccion', empresa.direccion || '');
        setValue('telefono', empresa.telefono || '');
        setValue('email', empresa.email || '');
        setValue('giro', empresa.giro || '');
        setValue('representante_legal', empresa.representante_legal || '');
        
        // Cargar configuraciones desde el backend
        if (empresa.configuracion) {
          setValue('permite_multiple_faenas', empresa.configuracion.permite_multiple_faenas || false);
          setValue('requiere_aprobacion_reportes', empresa.configuracion.requiere_aprobacion_reportes || false);
        } else {
          // Valores por defecto si no hay configuración
          setValue('permite_multiple_faenas', true);
          setValue('requiere_aprobacion_reportes', false);
        }
        
      } catch (err: any) {
        setError(err.response?.data?.message || 'Error al cargar datos de la empresa');
      } finally {
        setLoadingData(false);
      }
    };
    
    cargarDatosEmpresa();
  }, [isEditing, empresaId, isOpen, setValue]);

  const onSubmit = async (data: EmpresaFormData) => {
    try {
      setLoading(true);
      setError(null);

      const empresaData: CreateEmpresaRequest = {
        nombre: data.nombre,
        rut: data.rut,
        razon_social: data.razon_social,
        direccion: data.direccion,
        telefono: data.telefono || undefined,
        email: data.email || undefined,
        giro: data.giro,
        representante_legal: data.representante_legal,
        configuracion: {
          permite_multiple_faenas: data.permite_multiple_faenas,
          requiere_aprobacion_reportes: data.requiere_aprobacion_reportes,
          configuracion_kml: {}
        }
      };
      if (isEditing) {
        await EmpresasAdminService.actualizarEmpresa(empresaId, empresaData);
      } else {
        await EmpresasAdminService.crearEmpresa(empresaData);
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={handleClose}
        ></div>
        
        <div 
          className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? t('admin.edit_item') : t('admin.add')} {t('admin.companies')}
              </h3>
              <p className="text-sm text-gray-500">{t('common.details')}</p>
            </div>
            <button 
              onClick={handleClose} 
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            {loadingData ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <span className="ml-3 text-gray-600">{t('common.loading')}</span>
              </div>
            ) : (
              <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.name')} *
                  </label>
                  <input
                    type="text"
                    {...register('nombre')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="Ej: Forestal San Pedro"
                  />
                  {errors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{errors.nombre.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RUT *
                  </label>
                  <input
                    type="text"
                    {...register('rut')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="12.345.678-9"
                  />
                  {errors.rut && (
                    <p className="mt-1 text-sm text-red-600">{errors.rut.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Razon Social *
                </label>
                <input
                  type="text"
                  {...register('razon_social')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Forestal San Pedro S.A."
                />
                {errors.razon_social && (
                  <p className="mt-1 text-sm text-red-600">{errors.razon_social.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Giro *
                </label>
                <input
                  type="text"
                  {...register('giro')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Explotacion forestal y maderera"
                />
                {errors.giro && (
                  <p className="mt-1 text-sm text-red-600">{errors.giro.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Direccion *
                </label>
                <input
                  type="text"
                  {...register('direccion')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Av. Kennedy 1234, Las Condes, Santiago"
                />
                {errors.direccion && (
                  <p className="mt-1 text-sm text-red-600">{errors.direccion.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    {...register('telefono')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="+56912345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="contacto@empresa.cl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Representante Legal *
                </label>
                <input
                  type="text"
                  {...register('representante_legal')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Juan Perez Gonzalez"
                />
                {errors.representante_legal && (
                  <p className="mt-1 text-sm text-red-600">{errors.representante_legal.message}</p>
                )}
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Configuracion</h4>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('permite_multiple_faenas')}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label className="ml-3 text-sm text-gray-700">
                      Permitir multiples faenas simultaneas
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('requiere_aprobacion_reportes')}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label className="ml-3 text-sm text-gray-700">
                      Requiere aprobacion de reportes
                    </label>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{t('common.error')}</h3>
                      <p className="mt-1 text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
              {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || loadingData}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.saving')}
                </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                  {isEditing ? t('admin.edit_item') : t('common.create')} {t('admin.companies')}
                </>
              )}
            </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmpresaForm;
